// The DS record flow (SD35): the "Record a video" card (ui_kits/grackle-app/RecordDialog.jsx),
// the Countdown overlay and floating RecorderBar (App.jsx), then the share sheet's upload band
// once recording stops (ShareDialog.jsx, top half). §4 #15/#17/#18 own the upload half of this
// flow; §1 WP11 owns everything below.
//
// FR-RD7/FR-P2, the reason this file exists in this shape: recording (useRecorder) and
// uploading (UploadQueue) are two independent state machines wired together by one
// onChunk callback. A stalled or offline network can only ever affect the upload side —
// the record/stop buttons, the elapsed timer and the mic meter never await a network call.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Avatar as DsAvatar,
  Badge,
  Banner,
  Callout,
  Countdown,
  Icon,
  IconButton,
  Input,
  Kbd,
  LevelMeter,
  Logo,
  ModeTile,
  RecPill,
  RecorderBar,
  Select,
  ToggleRow,
  UploadStatus,
  formatHotkey,
} from "@grackle/ui";
import { isDevMode, subscribeDevMode } from "../lib/devMode";
import { useIsMobileLayout } from "../lib/mobileLayout";
import { useSession } from "../session";
import { Avatar, Button, Notice, PageLoader } from "../components/ui";
import MobileRecorderScreen, { type MobileCaptureChoice } from "../components/mobile/MobileRecorderScreen";
import MobileShareSheet from "../components/mobile/MobileShareSheet";
import { useRecorder, type SourceMode } from "./useRecorder";
import { ApiFail, api } from "../api/client";
import type { OrgPolicyOut } from "../api/types";
import { announceConsent, createLiveSession, finalizeLive } from "../live/client";
import { useLiveAudioTap } from "./useLiveAudioTap";
import MeetingStartPanel from "./MeetingStartPanel";
import { appLabel, type CapturePlatform, type MeetingApp } from "./meetingCapture";
import { useMeetingAutoStop } from "./useMeetingAutoStop";
import { DEFAULT_PART_SIZE, UploadQueue, type UploadQueueSnapshot } from "./uploadQueue";
import {
  appendDiskSegment,
  discardDiskSession,
  emitDesktop,
  EVT_HUD_STATE,
  EVT_MEETING_KEEP,
  EVT_NAVIGATE_LIBRARY,
  EVT_RECORD_MARK,
  EVT_RECORD_PAUSE,
  EVT_RECORD_START,
  EVT_RECORD_STOP,
  finishDiskSession,
  hudHide,
  hudShow,
  isDesktop,
  isMobileShell,
  loadMobileBridge,
  mobileCaptureStart,
  type MobileBridgeStatus,
  listenDesktop,
  listNativeSources,
  nativePreviewJpeg,
  loadDesktopInfo,
  peekDesktopInfo,
  recorderHotkeys,
  setNativeRecordingFlag,
  startDiskSession,
  stopDiskSession,
  type DesktopInfo,
  type HudState,
  type NativeCaptureSource,
  type RecoverableSession,
} from "../desktop";
import { deniedPermissionNotices, supportsSystemAudioCapture } from "./permissions";
import { requestTrayRefresh } from "../trayRefresh";
import DevModeUnlock from "../components/DevModeUnlock";
import PublishStatusPanel from "./PublishStatusPanel";
import RecorderDiagnosticsPanel from "./RecorderDiagnosticsPanel";
import { recDiag } from "./diagnostics";
import { markSessionActive, markSessionInactive } from "./recovery/activeSessions";
import { createLiveStatePersister } from "./recovery/livePersist";
import { diskLocalSource } from "./recovery/diskAdapter";
import { createRealQueueFactory, realRecoveryApi } from "./recovery/realQueue";
import { recoverSession } from "./recovery/stateMachine";
import type { PersistedUploadState } from "./recovery/types";
import { deleteLocalSession as deleteWebLocalSession, newRecordingId, putChunk } from "./persist/chunkStore";
import {
  CAMERA_FILTERS,
  bubbleScreenRadius,
  clampBubble,
  mediaFractionToPointer,
  pointerToMediaFraction,
  type CameraBubble,
  type CameraFilterId,
} from "./cameraFx";
import type { BlurState } from "./compositor";
import { renameGrackle } from "../library/api";
import { TITLE_MAX } from "../library/RenameGrackleDialog";
import { libraryPath } from "../library/routes";
import RegionPicker from "./RegionPicker";
import { regionSourceId } from "./regionCrop";
import "./recorder.css";

/** The five capture modes (DS artboard 1b, `ui_kits/desktop/RecorderScreen.jsx`'s `GK_MODES`).
 * "Full screen" and "Window" both drive the same `requestSource("screen")` call — on the web
 * there is no distinct API for the two, just the one browser picker (decision #1) — so they're
 * tracked as separate tile ids purely for which tile lights up, not separate recorder modes. */
type ModeTileId = "full" | "window" | "region" | "camera" | "pip";

interface ModeTileConfig {
  id: ModeTileId;
  icon: string;
  label: string;
  mode: SourceMode | null;
}

const MODE_TILES: ModeTileConfig[] = [
  { id: "full", icon: "desktop", label: "Full screen", mode: "screen" },
  { id: "window", icon: "browser", label: "Window", mode: "screen" },
  // F15a: Region is a native desktop capture (region picker -> `region:x,y,w,h`). It is enabled
  // only where `capabilities.region_capture` says the native grab can crop; elsewhere it stays
  // disabled with REGION_NOTE rather than silently falling back to something else.
  { id: "region", icon: "selection", label: "Region", mode: null },
  { id: "camera", icon: "user", label: "Camera only", mode: "camera" },
  { id: "pip", icon: "picture-in-picture", label: "Screen + camera", mode: "screen-camera" },
];
const REGION_NOTE = "Region capture is coming to the desktop app";
const REGION_NOTE_UNSUPPORTED = "Region capture isn't available on this system yet";

function makeTimestampTitle(): string {
  // Issue #18: use ISO-style format instead of locale-dependent toLocaleDateString
  // so titles are consistent regardless of user locale.
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const mins = String(now.getMinutes()).padStart(2, "0");
  return `Recording — ${month}-${day} ${hours}:${mins}`;
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

export default function RecorderPage() {
  const { user, orgs, loading: sessionLoading } = useSession();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [devMode, setDevMode] = useState(isDevMode());
  const [hideUpsell, setHideUpsell] = useState(() => localStorage.getItem("grackle-hide-upsell") === "true");
  const mobileLayout = useIsMobileLayout();
  // F16: the native bridge (mobile shell only) decides whether the Screen choice is offered.
  const [mobileBridge, setMobileBridge] = useState<MobileBridgeStatus | null>(null);
  const [mobileChoice, setMobileChoice] = useState<MobileCaptureChoice>("camera");
  const [mobileScreenError, setMobileScreenError] = useState<string | null>(null);
  useEffect(() => {
    if (!mobileLayout || !isMobileShell()) return;
    let live = true;
    void loadMobileBridge()
      .then((status) => {
        if (live) setMobileBridge(status);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [mobileLayout]);
  useEffect(() => subscribeDevMode(setDevMode), []);

  const orgParam = searchParams.get("org");
  const [chosenOrgId, setChosenOrgId] = useState<string | null>(null);
  const org = useMemo(() => {
    if (orgParam) return orgs.find((o) => o.slug === orgParam) ?? null;
    if (orgs.length === 1) return orgs[0];
    if (chosenOrgId) return orgs.find((o) => o.id === chosenOrgId) ?? null;
    return null;
  }, [orgs, orgParam, chosenOrgId]);

  // FR-L2: empty means "use the timestamp" — resolved when capture starts, since the
  // grackle (and the disk session) are named then. The stopped screen edits the same state and
  // renames the grackle on Continue if it changed.
  const [title, setTitle] = useState("");
  const startedTitleRef = useRef<string | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  // DS Countdown: a click on the scrim pauses the 3-2-1 ("Click anywhere to pause the countdown.").
  const [countdownPaused, setCountdownPaused] = useState(false);
  const [uploadSnapshot, setUploadSnapshot] = useState<UploadQueueSnapshot | null>(null);
  // PR6 3b/3c: "Stop opens the share sheet" (DS mobile recorder) — shown once there is a
  // grackle id to share, dismissed by hand from then on so a retried upload can't reopen it.
  const [mobileShareOpen, setMobileShareOpen] = useState(false);
  const [mobileShareDismissed, setMobileShareDismissed] = useState(false);
  const [desktopInfo, setDesktopInfo] = useState<DesktopInfo | null>(() => peekDesktopInfo());
  // Which tile is lit, not which SourceMode is active — see MODE_TILES' comment on why those
  // differ for "Full screen" vs "Window".
  const [pickerTileId, setPickerTileId] = useState<ModeTileId | null>(null);
  const systemAudioSupported = useMemo(() => supportsSystemAudioCapture(), []);
  const hotkeys = useMemo(() => recorderHotkeys(desktopInfo), [desktopInfo]);
  const [regionPick, setRegionPick] = useState<{ display: NativeCaptureSource; previewUrl: string } | null>(null);
  const uploadQueueRef = useRef<UploadQueue | null>(null);
  const diskSessionIdRef = useRef<string | null>(null);
  // FR-RD14: the plain web recorder's IndexedDB session id — parallel to diskSessionIdRef,
  // but only one of the two is ever set for a given recording (desktop vs. web).
  const webSessionIdRef = useRef<string | null>(null);
  const webChunkSeqRef = useRef(0);
  const liveStatePersisterRef = useRef<ReturnType<typeof createLiveStatePersister> | null>(null);
  const [crashProtectionOff, setCrashProtectionOff] = useState(false);
  // FR-RD14: after the first failed IndexedDB write (e.g. QuotaExceededError) this recording
  // stops writing locally, so whatever is stored stays a gap-free prefix a recovery could use.
  const webPersistBrokenRef = useRef(false);
  // FR-RD7/FR-RD14: each chunk is durable (fsynced to disk, or written to IndexedDB) before
  // the upload queue may touch the network. MediaRecorder can fire the last dataavailable and
  // then `stop` before those writes resolve, so handleStop awaits this chain.
  const pendingLocalWritesRef = useRef(Promise.resolve());
  // SD25 (FR-P3c): chunks go straight to the upload queue and nowhere else. The whole
  // recording used to be retained in memory as well, because the client's remux and encode
  // fallback both needed the complete Blob after stop — an hour of 1080p held in the tab
  // alongside everything else it was doing, which is exactly the pressure that decision
  // removed. The queue keeps only what it has not yet uploaded.
  //
  // FR-RD7 (desktop): the same chunk is also fsynced to local disk via the Tauri shell
  // *before* the network is required. A total network loss leaves a recoverable file.
  const handleChunk = useCallback((chunk: Blob) => {
    // FR-RD14: keeps this session "fresh" so no recovery sweep resumes it while it's live.
    liveStatePersisterRef.current?.touch();
    recDiag.chunk(chunk.size);
    const diskId = diskSessionIdRef.current;
    if (diskId) {
      const write = appendDiskSegment(diskId, chunk)
        .then(
          () => recDiag.count("saved"),
          () => {
            // Disk is a durability layer, not a gate — still enqueue so a write
            // error cannot drop the chunk from the in-flight upload.
            recDiag.count("saveFailed");
          },
        )
        .then(() => {
          uploadQueueRef.current?.push(chunk);
          recDiag.count("queued");
        });
      pendingLocalWritesRef.current = pendingLocalWritesRef.current.then(() => write);
      return;
    }
    const webId = webSessionIdRef.current;
    if (webId) {
      const seq = webChunkSeqRef.current++;
      // FR-RD14: write to IndexedDB *before* the upload queue, same ordering as the desktop
      // disk path above — a closed/crashed tab leaves a recoverable chunk set behind. Writes are
      // chained one after another so chunks reach the queue in capture order and a failure
      // can't leave a gap in what's stored.
      const write = pendingLocalWritesRef.current
        .then(() => (webPersistBrokenRef.current ? undefined : putChunk(webId, seq, chunk)))
        .then(
          () => recDiag.count("saved"),
          () => {
            // QuotaExceededError or any other IDB failure: keep uploading from memory rather
            // than dropping the chunk, but this recording can no longer survive a crash.
            recDiag.count("saveFailed");
            webPersistBrokenRef.current = true;
            setCrashProtectionOff(true);
          },
        )
        .then(() => {
          uploadQueueRef.current?.push(chunk);
          recDiag.count("queued");
        });
      pendingLocalWritesRef.current = write;
      return;
    }
    uploadQueueRef.current?.push(chunk);
    recDiag.count("queued");
  }, []);

  const recorder = useRecorder(handleChunk);

  // F8 / FR-RD15: `/record?meeting=<window id>&platform=zoom_desktop|teams_desktop&app=zoom|teams`
  // is how the detected-meeting banner hands a Zoom/Teams window to this same recorder. The
  // capture, disk-first segments, upload queue and recovery below are the ordinary pipeline.
  const meeting = useMemo(() => {
    const sourceId = searchParams.get("meeting");
    const platform = searchParams.get("platform");
    if (!sourceId || !isDesktop() || isMobileShell()) return null;
    if (platform !== "zoom_desktop" && platform !== "teams_desktop") return null;
    const app: MeetingApp = platform === "zoom_desktop" ? "zoom" : "teams";
    return { sourceId, platform: platform as CapturePlatform, app, windowTitle: searchParams.get("title") ?? "" };
  }, [searchParams]);
  const [meetingPolicy, setMeetingPolicy] = useState<OrgPolicyOut | null>(null);
  const [meetingError, setMeetingError] = useState<string | null>(null);
  const [meetingPanelKey, setMeetingPanelKey] = useState(0);
  const meetingAnnouncedRef = useRef<string | null>(null);
  const liveSessionIdRef = useRef<string | null>(null);
  const [liveSessionId, setLiveSessionId] = useState<string | null>(null);
  const meetingSourceRequested = useRef(false);

  useEffect(() => {
    if (!meeting || !org) return;
    api<OrgPolicyOut>("GET", `/orgs/${org.id}/policy`)
      .then(setMeetingPolicy)
      .catch(() => setMeetingPolicy(null)); // defaults below: all-party, policy allowed (server still enforces)
  }, [meeting, org]);

  useEffect(() => {
    if (!meeting || meetingSourceRequested.current || recorder.phase !== "idle" || !desktopInfo) return;
    meetingSourceRequested.current = true;
    setTitle(`${appLabel(meeting.app)} meeting — ${makeTimestampTitle().replace("Recording — ", "")}`);
    setPickerTileId("window");
    // Native window capture where wired; otherwise the webview picker (instruction shown below).
    void recorder.requestSource("screen", { nativeSourceId: meeting.sourceId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting, recorder.phase, desktopInfo]);

  const meetingCapturing = recorder.phase === "recording" || recorder.phase === "paused";
  useLiveAudioTap(meetingCapturing ? liveSessionId : null, recorder.previewStream ?? null, recorder.phase === "paused");
  const stopRef = useRef<() => void>(() => {});
  const autoStop = useMeetingAutoStop({
    active: Boolean(meeting) && meetingCapturing,
    sourceId: meeting?.sourceId ?? null,
    onStop: () => stopRef.current(),
  });

  useEffect(() => {
    void loadDesktopInfo({ refresh: true }).then(setDesktopInfo);
  }, []);

  // PR6 3b: the mobile layout skips the screen/camera/screen+camera picker — a phone webview
  // has nothing to pick — and goes straight to camera + mic, matching "camera and mic capture"
  // (the brief's mobile recorder scope; native capture stays out of scope here, SD33/R5).
  useEffect(() => {
    if (mobileLayout && recorder.phase === "idle") void recorder.requestSource("camera");
  }, [mobileLayout, recorder.phase, recorder.requestSource]);

  // Gated on phase "stopped": the progressive upload (SD25) creates the grackle as soon as
  // capture starts, so a grackle id alone would pop the sheet mid-recording and its modal
  // scrim would cover the Stop button. The DS flow is "Stop opens the share sheet".
  useEffect(() => {
    if (mobileLayout && recorder.phase === "stopped" && uploadSnapshot?.grackleId && !mobileShareDismissed) {
      setMobileShareOpen(true);
    }
  }, [mobileLayout, recorder.phase, uploadSnapshot?.grackleId, mobileShareDismissed]);

  // Countdown ticks down to 0, then actually starts capture. Recording only begins once this
  // reaches zero — the countdown itself never touches the recorder or the network.
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      setCountdown(null);
      setCountdownPaused(false);
      beginCapture();
      return;
    }
    if (countdownPaused) return;
    // One second per digit (DS Countdown: "the caller ticks it once per second").
    const t = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, countdownPaused]);

  const desktopOnly = isDesktop() && !isMobileShell();

  const regionReady = Boolean(desktopInfo?.capabilities.region_capture);
  const tileEnabled = (tile: ModeTileConfig) => Boolean(tile.mode) || (tile.id === "region" && regionReady);

  async function openRegionPicker() {
    try {
      const sources = await listNativeSources();
      const display = sources.find((s) => s.kind === "display");
      if (!display) return;
      const jpeg = await nativePreviewJpeg(display.id);
      setRegionPick({ display, previewUrl: `data:image/jpeg;base64,${jpeg}` });
    } catch {
      setRegionPick(null);
    }
  }

  function selectTile(tile: ModeTileConfig) {
    if (tile.id === "region" && regionReady) {
      void openRegionPicker();
      return;
    }
    if (!tile.mode) return;
    setPickerTileId(tile.id);
    void recorder.requestSource(tile.mode);
  }

  async function beginCapture() {
    if (!org) return;
    const contentType = recorder.mimeType.startsWith("video/mp4") ? "video/mp4" : "video/webm";
    const resolvedTitle = title.trim() || makeTimestampTitle();
    setTitle(resolvedTitle);
    startedTitleRef.current = resolvedTitle;
    if (meeting) {
      // F8 / FR-RD15: create the meeting-type live session first so the org policy
      // (403 desktop_meeting_capture_disabled) is checked before any capture starts. It is
      // linked to the grackle at finalize (handleStop); recording itself never waits on this
      // beyond the check, and a non-policy failure does not block capture (FR-RD7).
      try {
        const live = await createLiveSession(org.id, {
          title: resolvedTitle,
          consent_acknowledged: true,
          capture_platform: meeting.platform,
        });
        liveSessionIdRef.current = live.id;
        setLiveSessionId(live.id);
        if (meetingAnnouncedRef.current) void announceConsent(live.id).catch(() => {});
      } catch (err) {
        if (err instanceof ApiFail && err.code === "desktop_meeting_capture_disabled") {
          setMeetingError("Desktop meeting capture is turned off for this organization — ask an organization admin.");
          setMeetingPanelKey((k) => k + 1);
          return;
        }
        if (err instanceof ApiFail && err.status === 403) {
          setMeetingError(err.message);
          setMeetingPanelKey((k) => k + 1);
          return;
        }
        liveSessionIdRef.current = null;
      }
    }
    const desktopSession = isDesktop();
    setCrashProtectionOff(false);

    // FR-RD7: open the on-disk session *before* MediaRecorder starts so the first 1s
    // timeslice cannot arrive with nowhere to fsync. FR-RD14: whichever local session id this
    // recording gets (disk or IndexedDB) is also the id the upload-state sidecar/row is
    // persisted under, so recovery/stateMachine.ts can find it next launch.
    let localSessionId: string | null = null;
    if (desktopSession) {
      try {
        const session = await startDiskSession(recorder.mimeType || contentType, resolvedTitle);
        diskSessionIdRef.current = session.id;
        localSessionId = session.id;
      } catch {
        // Disk session is a durability layer, not a gate: capture still starts, just without
        // a local-disk recovery path for this one recording.
      }
      void setNativeRecordingFlag(true);
    } else {
      localSessionId = newRecordingId();
      webSessionIdRef.current = localSessionId;
      webChunkSeqRef.current = 0;
      webPersistBrokenRef.current = false;
    }

    liveStatePersisterRef.current = null;
    if (localSessionId) {
      // FR-RD14: this session is being actively recorded by this tab right now — a recovery
      // sweep (RecoveryProvider) must not also try to resume it concurrently.
      markSessionActive(localSessionId);
      const initialState: PersistedUploadState = {
        localSessionId,
        userId: user?.id,
        orgId: org.id,
        title: resolvedTitle,
        contentType,
        grackleId: null,
        uploadId: null,
        partSize: DEFAULT_PART_SIZE,
        completedParts: [],
        finalized: false,
        status: "recording",
        updatedAt: Date.now(),
      };
      liveStatePersisterRef.current = createLiveStatePersister(initialState);
    }

    const queue = new UploadQueue(org.id, resolvedTitle, contentType, {
      onSnapshot: setUploadSnapshot,
      onSessionCreated: liveStatePersisterRef.current?.onSessionCreated,
      onPartAccepted: liveStatePersisterRef.current?.onPartAccepted,
    });
    uploadQueueRef.current = queue;
    queue.start();

    recorder.startRecording();
    // Decision #4: the HUD takes over as the on-screen indicator once capture actually starts
    // (not at the start of the 3-2-1 countdown — that's still worth watching in this window).
    if (desktopOnly) void hudShow();
  }

  async function handleStartClick() {
    // F16 (SD47): Screen hands the session to the native writer; the bridge refuses (with its
    // own copy) unless `screen.ready`, so a stale tap can't start a screen recording that
    // nothing will write.
    if (mobileLayout && mobileChoice === "screen") {
      setMobileScreenError(null);
      try {
        setMobileBridge(await mobileCaptureStart({ mode: "screen", includeMic: true }));
      } catch (err) {
        setMobileScreenError(err instanceof Error ? err.message : String(err));
        return;
      }
    }
    setCountdown(3);
  }

  async function handleStop() {
    await recorder.stopRecording(); // resolves once the final chunk has reached handleChunk
    // Decision #4: close the HUD and bring the main window back as soon as capture has actually
    // stopped, rather than waiting on the upload below — the share-after-stop screen (PR3)
    // renders from `recorder.phase` alone and shouldn't wait on the network either.
    // FR-RD15: the Rust side refuses `hud_hide` while the recording flag is set, so clear it first.
    await setNativeRecordingFlag(false);
    if (desktopOnly) void hudHide();
    await pendingLocalWritesRef.current; // FR-RD7/FR-RD14: last chunk is durable before finish
    // FR-RD14 scope item 4 (mobile/offline publish): stopping while offline can't be told
    // apart from "briefly slow" at this instant, so this only labels the persisted state —
    // the live queue keeps retrying either way, and a real online/visibility/backoff trigger
    // (RecoveryProvider) picks this session back up once connectivity returns.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      liveStatePersisterRef.current?.markPendingUpload("offline when recording stopped");
    }
    void setNativeRecordingFlag(false);
    const diskId = diskSessionIdRef.current;
    if (diskId) {
      await stopDiskSession(diskId).catch(() => {});
    }
    const webId = webSessionIdRef.current;

    const queue = uploadQueueRef.current;
    if (!queue) return;
    try {
      // SD25/FR-P3c: `finish()` is the end of the browser's involvement with this recording.
      // `/complete` enqueues the one `publish` job that produces the audio track, the poster
      // and — if a packet copy fits FR-P12's budget — the HLS segments. There is nothing to
      // keep this tab open for, which is the entire point of the decision.
      const finished = await queue.finish();
      const liveId = liveSessionIdRef.current;
      liveSessionIdRef.current = null;
      setLiveSessionId(null);
      if (liveId) void finalizeLive(liveId, finished.id).catch(() => {}); // post-call F4 transcript + F1 notes
      liveStatePersisterRef.current?.finalize();
      // FR-RD14: local data is deleted only now that `complete` has actually succeeded.
      if (diskId) await finishDiskSession(diskId).catch(() => {});
      if (webId) await deleteWebLocalSession(webId).catch(() => {});
      requestTrayRefresh(); // decision #5: this org's library just gained a grackle
    } catch {
      // surfaced via onSnapshot, not this promise — local data (disk segments or IndexedDB
      // chunks) stays put either way, so RecoveryProvider can retry on next launch.
    }
    if (diskId) markSessionInactive(diskId);
    if (webId) markSessionInactive(webId);
    diskSessionIdRef.current = null;
    webSessionIdRef.current = null;
    liveStatePersisterRef.current = null;
  }

  function handleDiscard() {
    void uploadQueueRef.current?.cancel();
    uploadQueueRef.current = null;
    setUploadSnapshot(null);
    const diskId = diskSessionIdRef.current;
    diskSessionIdRef.current = null;
    if (diskId) {
      markSessionInactive(diskId);
      void discardDiskSession(diskId);
    }
    const webId = webSessionIdRef.current;
    webSessionIdRef.current = null;
    if (webId) {
      markSessionInactive(webId);
      void deleteWebLocalSession(webId);
    }
    liveStatePersisterRef.current = null;
    setCrashProtectionOff(false);
    void setNativeRecordingFlag(false);
    recorder.reset();
    setTitle("");
    startedTitleRef.current = null;
    setRenameError(null);
    setMobileShareOpen(false);
    setMobileShareDismissed(false);
  }

  /** "Upload now" on a recoverable-on-disk session (FR-RD14): runs the same resume state
   * machine RecoveryProvider runs automatically on launch, for just this one session. */
  async function handleUploadNow(session: RecoverableSession) {
    await recoverSession(
      session.id,
      {
        localSource: diskLocalSource,
        queueFactory: createRealQueueFactory(),
        api: realRecoveryApi,
        ownerId: user?.id,
      },
      { retryFailed: true },
    );
    setDesktopInfo(await loadDesktopInfo({ refresh: true }));
  }

  async function handleDiscardRecoverable(sessionId: string) {
    if (!window.confirm("Discard this recoverable recording? This can't be undone.")) return;
    await discardDiskSession(sessionId);
    setDesktopInfo(await loadDesktopInfo({ refresh: true }));
  }

  stopRef.current = () => {
    if (recorder.phase === "recording" || recorder.phase === "paused") void handleStop();
  };

  function handlePauseToggle() {
    if (recorder.phase === "paused") recorder.resumeRecording();
    else recorder.pauseRecording();
  }

  useEffect(() => {
    if (!isDesktop()) return;
    const unsubs: Array<() => void> = [];
    // decision #3: these three also fire from the HUD's own buttons (same event names), from
    // the tray, and from the global hotkeys — this one listener drives capture regardless of
    // which of the four raised it.
    void listenDesktop(EVT_RECORD_START, () => {
      if (recorder.phase === "ready" && countdown === null) setCountdown(3);
    }).then((u) => unsubs.push(u));
    void listenDesktop(EVT_RECORD_STOP, () => {
      if (recorder.phase === "recording" || recorder.phase === "paused") void handleStop();
    }).then((u) => unsubs.push(u));
    void listenDesktop(EVT_RECORD_PAUSE, () => {
      if (recorder.phase === "recording" || recorder.phase === "paused") handlePauseToggle();
    }).then((u) => unsubs.push(u));
    // F15a: the mark-moment hotkey bookmarks the current time on the live session (F17
    // bookmarks), when one is running; otherwise there is nothing to attach a mark to.
    void listenDesktop(EVT_RECORD_MARK, () => {
      const liveId = liveSessionIdRef.current;
      if (!liveId || recorder.phase !== "recording") return;
      void api("POST", `/live-transcripts/${liveId}/bookmarks`, { at_ms: Math.round(recorder.elapsedMs) }).catch(() => {});
    }).then((u) => unsubs.push(u));
    void listenDesktop(EVT_MEETING_KEEP, () => autoStop.keep()).then((u) => unsubs.push(u));
    void listenDesktop(EVT_NAVIGATE_LIBRARY, () => {
      navigate(org ? libraryPath(org.slug) : "/orgs");
    }).then((u) => unsubs.push(u));
    return () => {
      for (const u of unsubs) u();
    };
    // Intentionally depend on the live recorder phase so tray/hotkey handlers see it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.phase, countdown, org, navigate]);

  // decision #3: broadcasts elapsed/paused/level/upload to the HUD window at a fixed rate
  // (independent of how often those values actually change — micLevels ticks every animation
  // frame) rather than on every render, to stay in the 4-10 Hz band the HUD needs and no more.
  const hudStateRef = useRef<HudState>({
    elapsedMs: 0,
    paused: false,
    level: 0,
    upload: { state: "uploading", partsAccepted: 0, partsCut: 0 },
  });
  const isCapturing = recorder.phase === "recording" || recorder.phase === "paused";
  hudStateRef.current = {
    ...(meeting ? { meeting: { app: meeting.app, closingIn: autoStop.closingIn } } : {}),
    elapsedMs: recorder.elapsedMs,
    paused: recorder.phase === "paused",
    level: recorder.micLevels.length
      ? recorder.micLevels.reduce((sum, level) => sum + level, 0) / recorder.micLevels.length
      : 0,
    upload: {
      state: uploadSnapshot?.phase === "done" ? "done" : "uploading",
      partsAccepted: uploadSnapshot?.partsAccepted ?? 0,
      partsCut: uploadSnapshot?.partsCut ?? 0,
    },
  };
  useEffect(() => {
    if (!desktopOnly || !isCapturing) return;
    const id = setInterval(() => void emitDesktop(EVT_HUD_STATE, hudStateRef.current), 150);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktopOnly, isCapturing]);

  async function handleContinue() {
    const grackleId = uploadSnapshot?.grackleId;
    if (!grackleId) return;
    const next = title.trim();
    if (next && next !== startedTitleRef.current) {
      setRenaming(true);
      setRenameError(null);
      try {
        await renameGrackle(grackleId, next);
      } catch (err) {
        // The recording is safe either way; keep the user here so the new title isn't lost.
        setRenameError(err instanceof ApiFail ? err.message : "Couldn't save the title.");
        setRenaming(false);
        return;
      }
      setRenaming(false);
    }
    navigate(`/record/${grackleId}/processing`);
  }

  if (sessionLoading) {
    return <PageLoader label="Preparing recorder…" />;
  }
  if (orgs.length === 0) {
    return <div className="center-page">You need an organization before you can record. <Link to="/orgs/new">Create one</Link>.</div>;
  }
  if (!org) {
    return (
      <div className="rec-shell">
        <div className="rec-topbar">
          <Logo height={22} />
        </div>
        <div className="rec-body">
          <div className="rec-org-picker">
            <div className="gk-overline rec-org-picker-label">Record into which organization?</div>
            <div className="choice-list">
              {orgs.map((o) => (
                <button key={o.id} className="gk-card gk-card--sm gk-card--interactive choice-row" onClick={() => setChosenOrgId(o.id)}>
                  <DsAvatar
                    square
                    size={32}
                    initials={o.name.trim().slice(0, 1).toUpperCase()}
                    color="var(--surface-inverse)"
                    style={{ color: "var(--now)", fontSize: 14 }}
                    aria-hidden
                  />
                  <div className="choice-body">
                    <div className="choice-title">{o.name}</div>
                  </div>
                  <span className="gk-btn gk-btn--primary gk-btn--sm choice-cta">Choose</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const phase = recorder.phase;
  const capturing = phase === "recording" || phase === "paused";
  const desktop = Boolean(desktopInfo);
  const micLevel = recorder.micLevels.length
    ? recorder.micLevels.reduce((sum, level) => sum + level, 0) / recorder.micLevels.length
    : 0;

  if (mobileLayout) {
    return (
      <>
        <MobileRecorderScreen
          phase={phase}
          countdown={countdown}
          title={title}
          previewStream={recorder.previewStream}
          micLevels={recorder.micLevels}
          elapsedMs={recorder.elapsedMs}
          error={recorder.error ?? mobileScreenError}
          uploadSnapshot={uploadSnapshot}
          onStart={() => void handleStartClick()}
          onStop={() => void handleStop()}
          onPauseToggle={handlePauseToggle}
          onDiscard={handleDiscard}
          screenBridge={mobileBridge?.screen ?? null}
          captureChoice={mobileChoice}
          onCaptureChoice={setMobileChoice}
        />
        {mobileShareOpen && uploadSnapshot?.grackleId ? (
          <MobileShareSheet
            grackleId={uploadSnapshot.grackleId}
            title={title}
            onClose={() => {
              setMobileShareOpen(false);
              setMobileShareDismissed(true);
              navigate(`/g/${uploadSnapshot.grackleId}`);
            }}
          />
        ) : null}
      </>
    );
  }

  // The "Record a video" dialog's Close and Cancel go where the page's back goes: the previous
  // in-app location when there is one (React Router keys the initial entry "default"), else
  // the org's library.
  const closePage = () => {
    if (location.key !== "default") navigate(-1);
    else navigate(libraryPath(org.slug));
  };

  const uploadProgress =
    uploadSnapshot?.phase === "done"
      ? 1
      : uploadSnapshot && uploadSnapshot.bytesRecorded > 0
        ? Math.min(1, uploadSnapshot.bytesAccepted / uploadSnapshot.bytesRecorded)
        : 0;
  const chosenMic = recorder.microphones.find((m) => m.deviceId === recorder.microphoneId) ?? recorder.microphones[0];

  const previewCaption =
    phase === "priming"
      ? "Requesting access…"
      : recorder.mode === "camera" || recorder.mode === "screen-camera"
        ? "Camera preview"
        : "Choose a source to preview it here";

  const topbar = (
    <div className="rec-topbar">
      <Logo height={22} />
      <DevModeUnlock />
      <div className="spacer" />
      <span className="rec-org-name">{org.name}</span>
      {user ? <Avatar name={user.name} email={user.email} size={26} /> : null}
    </div>
  );

  if (phase === "stopped") {
    // DS share sheet, top half (ShareDialog.jsx): the upload band, the title, then the
    // actions. The link exists as soon as the grackle does (SD25 progressive upload), which is
    // the only time the DS line about the link is true — before that, the honest wording.
    const failed = uploadSnapshot?.phase === "failed";
    const done = uploadSnapshot?.phase === "done";
    const statusLine = failed
      ? "Upload failed"
      : done
        ? "Uploaded"
        : uploadSnapshot?.retrying
          ? `Uploading — ${Math.round(uploadProgress * 100)}% · retrying${uploadSnapshot.lastError ? ` (${uploadSnapshot.lastError})` : ""}`
          : `Uploading — ${Math.round(uploadProgress * 100)}%`;
    return (
      <div className="rec-shell">
        {topbar}
        <div className="rec-body">
          <section className="rec-card" aria-labelledby="rec-stopped-title">
            <header className="rec-card__head">
              <Logo variant="mark" height={28} />
              <div className="rec-card__titles">
                <h1 className="rec-card__title" id="rec-stopped-title">
                  {uploadSnapshot?.grackleId ? "Your video is ready to send" : "Saving your recording"}
                </h1>
              </div>
            </header>

            <div className="rec-band" data-testid="upload-band">
              <div className="rec-band__thumb">
                {recorder.elapsedMs > 0 ? (
                  <Badge tone="glass" size="sm" className="rec-band__duration">
                    {formatElapsed(recorder.elapsedMs)}
                  </Badge>
                ) : null}
              </div>
              <div className="rec-band__body">
                <div className="rec-band__status" role="status">
                  {statusLine}
                </div>
                <div className="rec-band__bar" aria-hidden>
                  <span className="rec-band__fill" style={{ width: `${Math.round(uploadProgress * 100)}%` }} />
                </div>
                <div className="rec-band__note">
                  {uploadSnapshot?.grackleId
                    ? "The link works right now — it starts playing as soon as the upload finishes."
                    : "Recording continues to upload in the background even if this leaves the screen — the network was never in the way of capturing it."}
                </div>
              </div>
            </div>

            {failed ? (
              uploadSnapshot.lastErrorCode === "quota_exceeded" ? (
                // FR-O10: a quota is a limit, not a fault — say so and who can lift it.
                <Callout tone="now" icon="hourglass-high" data-testid="quota-callout">
                  {uploadSnapshot.lastError}
                </Callout>
              ) : (
                <Notice tone="error">{uploadSnapshot.lastError ?? "The upload failed."}</Notice>
              )
            ) : null}

            <Input label="Title" value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} />
            {renameError ? <Notice tone="error">{renameError}</Notice> : null}

            <div className="rec-card__foot">
              <div className="spacer" />
              <Button variant="danger" onClick={handleDiscard}>
                Discard
              </Button>
              <Button onClick={() => void handleContinue()} disabled={!uploadSnapshot?.grackleId} loading={renaming}>
                Continue
              </Button>
            </div>
          </section>

          {devMode ? <RecorderDiagnosticsPanel /> : null}
          {devMode ? <PublishStatusPanel grackleId={uploadSnapshot?.grackleId ?? null} /> : null}
        </div>
      </div>
    );
  }

  const deviceSelectDisabled = capturing;

  return (
    <div className="rec-shell">
      {topbar}

      <div className="rec-body">
        <section className={`rec-card${capturing ? "" : " rec-card--wide"}`} aria-labelledby="rec-title">
          <header className="rec-card__head">
            <Logo variant="mark" height={28} />
            <div className="rec-card__titles">
              <h1 className="rec-card__title" id="rec-title">
                Record a video
              </h1>
              <p className="rec-card__sub">
                {desktop
                  ? "Record on desktop — segments write to disk first, so a dropped network cannot lose the file."
                  : "Nothing is captured until you press record."}
              </p>
            </div>
            {!capturing ? <IconButton icon="x" label="Close" variant="muted" onClick={closePage} /> : null}
          </header>

          <div className="rec-preview" role="group" aria-label="Preview">
            {recorder.previewStream ? (
              <PreviewVideo stream={recorder.previewStream} />
            ) : (
              <div className="rec-preview-placeholder">{previewCaption}</div>
            )}
            {recorder.mode === "screen-camera" &&
            recorder.cameraBubble &&
            recorder.overlaySize &&
            (phase === "ready" || capturing) ? (
              <BubbleDragLayer
                bubble={recorder.cameraBubble}
                overlaySize={recorder.overlaySize}
                onChange={recorder.setCameraBubble}
              />
            ) : null}
            {phase === "ready" || phase === "priming" ? (
              <span className="rec-preview-badge gk-overline">Preview</span>
            ) : null}
            {capturing ? (
              <span className="rec-preview-recpill">
                <RecPill
                  variant="solid"
                  label={`${phase === "paused" ? "Paused" : "REC"} ${formatElapsed(recorder.elapsedMs)}`}
                />
              </span>
            ) : null}
          </div>

          {recorder.error ? <Notice tone="error">{recorder.error}</Notice> : null}

          {capturing ? (
            <>
              {crashProtectionOff ? (
                <Callout tone="danger" icon="warning-circle">
                  Crash protection is off for this recording — a crash now would lose what
                  hasn&rsquo;t uploaded yet.
                </Callout>
              ) : null}
              {/* The DS floating ink pill (App.jsx's RecorderToolbar): stop is the only red
                  control. Restart is not wired: there is no restart in this recorder — a
                  restart would have to discard and re-pick the source. */}
              <div className="rec-hud" data-testid="recorder-bar">
                <RecorderBar
                  time={formatElapsed(recorder.elapsedMs)}
                  paused={phase === "paused"}
                  level={micLevel}
                  pauseHotkey={hotkeys.pause}
                  stopHotkey={hotkeys.startStop}
                  onPause={handlePauseToggle}
                  onStop={() => void handleStop()}
                  onDiscard={handleDiscard}
                />
              </div>
              <div className="rec-upload-line">
                <UploadStatus
                  state={uploadSnapshot?.phase === "done" ? "done" : "uploading"}
                  progress={uploadProgress}
                  label={
                    // FR-RD14: during recording, say how many parts have made it to the server.
                    // …and when they aren't, why: a stuck count with no reason is what made a
                    // closed upload look like a slow one for a whole recording.
                    uploadSnapshot?.phase === "failed"
                      ? `Upload stopped — ${uploadSnapshot.lastError ?? "the upload failed"}`
                      : uploadSnapshot && uploadSnapshot.partsCut > 0
                        ? `${uploadSnapshot.partsAccepted} of ${uploadSnapshot.partsCut} parts uploaded${
                            uploadSnapshot.retrying ? " · retrying" : ""
                          }`
                        : undefined
                  }
                />
                <span className="gk-caption">
                  {desktop
                    ? "Chunks are on disk already. Closing this window will not stop the recording."
                    : "Recording keeps going even if your connection drops."}
                </span>
              </div>
              {meeting && autoStop.closingIn !== null ? (
                <Callout tone="now" icon="info" role="status">
                  The {appLabel(meeting.app)} meeting window closed — stopping in {autoStop.closingIn}s.{" "}
                  <Button size="sm" variant="secondary" onClick={autoStop.keep}>
                    Keep recording
                  </Button>
                </Callout>
              ) : null}
            </>
          ) : null}

          {!capturing ? (
            <div className="rec-sources">
              <div className="gk-overline">Sources</div>

              {regionPick ? (
                <RegionPicker
                  display={regionPick.display}
                  previewUrl={regionPick.previewUrl}
                  onCancel={() => setRegionPick(null)}
                  onPick={(region) => {
                    setRegionPick(null);
                    setPickerTileId("region");
                    void recorder.requestSource("screen", { nativeSourceId: regionSourceId(region) });
                  }}
                />
              ) : null}

              <div className="rec-mode-grid">
                {MODE_TILES.map((tile) => (
                  <div className="rec-mode-cell" key={tile.id}>
                    <ModeTile
                      icon={tile.icon}
                      label={tile.label}
                      selected={pickerTileId === tile.id}
                      disabled={!tileEnabled(tile) || countdown !== null}
                      aria-describedby={!tileEnabled(tile) ? `${tile.id}-note` : undefined}
                      onClick={() => selectTile(tile)}
                    />
                    {!tileEnabled(tile) ? (
                      <p id={`${tile.id}-note`} className="gk-caption rec-mode-note">
                        {desktopInfo ? REGION_NOTE_UNSUPPORTED : REGION_NOTE}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>

              {!desktop && (pickerTileId === "full" || pickerTileId === "window") ? (
                <Callout icon="info">
                  Full screen and Window both open your browser&rsquo;s screen-share picker —
                  choose the source there.
                </Callout>
              ) : null}
            </div>
          ) : null}

          <div className="rec-devices">
            {!capturing ? (
              <div className="rec-title-field">
                <Input
                  label="Title"
                  value={title}
                  placeholder="Recording — date and time"
                  maxLength={TITLE_MAX}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
            ) : null}
            {recorder.mode === "screen" && recorder.nativeSources.length > 0 ? (
              <ToggleRow
                icon={pickerTileId === "window" ? "browser" : "desktop"}
                label={recorder.usingNative ? "Native display / window" : "Display / window"}
                trailing={
                  <Select
                    size="sm"
                    aria-label="Display or window"
                    className="rec-device-select"
                    value={recorder.nativeSourceId ?? ""}
                    onChange={(v) => recorder.setNativeSourceId(v)}
                    disabled={deviceSelectDisabled}
                    options={recorder.nativeSources.map((s) => ({ value: s.id, label: s.label }))}
                  />
                }
              />
            ) : null}

            {(recorder.mode === "camera" || recorder.mode === "screen-camera") && recorder.cameras.length > 0 ? (
              <ToggleRow
                icon="video-camera"
                label="Camera"
                trailing={
                  <Select
                    size="sm"
                    aria-label="Camera"
                    className="rec-device-select"
                    value={recorder.cameraId ?? ""}
                    onChange={(v) => recorder.setCameraId(v)}
                    options={recorder.cameras.map((c) => ({ value: c.deviceId, label: c.label }))}
                  />
                }
              />
            ) : null}

            {recorder.mode === "camera" || recorder.mode === "screen-camera" ? (
              <ToggleRow
                icon="sparkle"
                label="Camera effect"
                description={
                  recorder.cameraEffectsLocked
                    ? "Choose an effect before you start recording."
                    : (BLUR_HINTS[recorder.blurState] ?? undefined)
                }
                trailing={
                  // Enabled while recording whenever the compositor is running: it applies this
                  // on its next frame without touching the captured track (FR-RD3). Camera-only
                  // without an effect records the camera directly, so there it waits for Stop.
                  <Select
                    size="sm"
                    aria-label="Camera effect"
                    className="rec-device-select"
                    disabled={recorder.cameraEffectsLocked}
                    value={recorder.cameraFilter}
                    onChange={(v) => recorder.setCameraFilter(v as CameraFilterId)}
                    options={CAMERA_FILTERS.map((f) => ({ value: f.id, label: f.label }))}
                  />
                }
              />
            ) : null}

            {recorder.mode === "screen-camera" ? (
              <div className="gk-caption rec-devices__note">
                Drag the camera bubble on the preview to move it — during recording too.
              </div>
            ) : null}

            <ToggleRow
              icon="microphone"
              label="Microphone"
              unavailable={!recorder.mode}
              trailing={
                <div className="rec-device-trailing">
                  {recorder.microphones.length > 0 ? (
                    <Select
                      size="sm"
                      aria-label="Microphone"
                      className="rec-device-select"
                      value={recorder.microphoneId ?? ""}
                      onChange={(v) => recorder.setMicrophoneId(v)}
                      options={recorder.microphones.map((m) => ({ value: m.deviceId, label: m.label }))}
                    />
                  ) : null}
                  {recorder.mode ? <LevelMeter size="sm" level={phase === "paused" ? 0 : micLevel} /> : null}
                </div>
              }
            />

            {recorder.mode === "screen" || recorder.mode === "screen-camera" ? (
              <ToggleRow
                icon="waveform"
                label="System audio"
                unavailable={!desktop && !systemAudioSupported}
                description={
                  !desktop && !systemAudioSupported
                    ? "Not available in this browser"
                    : recorder.systemAudioCaptured === null
                      ? "Checking…"
                      : recorder.systemAudioCaptured
                        ? "Included from your screen share"
                        : desktop
                          ? (desktopInfo?.capabilities.audio.system_loopback_ready
                              ? "Native loopback will mix speakers into this recording."
                              : desktopInfo?.capabilities.native_loopback.reason ??
                                "Not included — check “Share audio” in the picker if the OS offers it.")
                          : `Not included — check "Share audio" (or "Share tab audio") in the picker next time.`
                }
                trailing={
                  recorder.systemAudioCaptured !== null ? (
                    <Badge tone={recorder.systemAudioCaptured ? "success" : "neutral"} icon={recorder.systemAudioCaptured ? "check" : undefined}>
                      {recorder.systemAudioCaptured ? "On" : "No"}
                    </Badge>
                  ) : (
                    <span />
                  )
                }
              />
            ) : null}
          </div>

          {desktop && desktopInfo ? (
            <Notice>
              {desktopInfo.capabilities.native_capture_ready
                ? `Native screen capture is on (${desktopInfo.capabilities.native_screen.backend}, ${desktopInfo.capabilities.session_kind}). Camera overlay still uses the shared recorder.`
                : `Using the shared recorder (screen / window / camera via the webview).${
                    desktopInfo.capabilities.native_screen.reason
                      ? ` ${desktopInfo.capabilities.native_screen.reason}`
                      : ""
                  }`}
              {desktopInfo.capabilities.audio.system_loopback
                ? ` System audio backend: ${desktopInfo.capabilities.audio.system_loopback_backend}${
                    desktopInfo.capabilities.audio.system_loopback_ready
                      ? " — ready."
                      : desktopInfo.capabilities.native_loopback.reason
                        ? ` — ${desktopInfo.capabilities.native_loopback.reason}`
                        : " — not ready; share system audio from the picker if the OS offers it."
                  }`
                : ""}
            </Notice>
          ) : null}
          {desktop && desktopInfo
            ? deniedPermissionNotices(desktopInfo.capabilities).map((n) => (
                <Notice key={n.id}>{n.text}</Notice>
              ))
            : null}
          {/* FR-RD14: RecoveryProvider (mounted app-wide) already auto-resumes these on
              launch — these controls are for acting sooner, or if auto-resume couldn't
              (e.g. offline). */}
          {desktopOnly && desktopInfo && desktopInfo.recoverable.length > 0
            ? desktopInfo.recoverable.map((s) => (
                <Callout key={s.id} tone="info" icon="cloud-arrow-up">
                  <div>
                    Recoverable on disk: {s.title} ({formatBytes(s.bytes_on_disk)}). Network loss did not
                    delete it.
                  </div>
                  <div className="rec-callout-actions">
                    <Button size="sm" variant="secondary" onClick={() => void handleUploadNow(s)}>
                      Upload now
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => void handleDiscardRecoverable(s.id)}>
                      Discard
                    </Button>
                  </div>
                </Callout>
              ))
            : null}
          {isMobileShell() && desktopInfo && desktopInfo.recoverable.length > 0 ? (
            <Notice>Waiting to upload ({desktopInfo.recoverable.length})</Notice>
          ) : null}

          {meeting && !capturing && countdown === null ? (
            <>
              {!recorder.usingNative && phase !== "idle" ? (
                <Callout tone="info" icon="info">
                  In the share picker, choose the {appLabel(meeting.app)} meeting window
                  {meeting.windowTitle ? ` (“${meeting.windowTitle}”)` : ""} and tick “Share audio”.
                </Callout>
              ) : null}
              <MeetingStartPanel
                key={meetingPanelKey}
                app={meeting.app}
                orgName={org.name}
                consentMode={meetingPolicy?.meeting_consent_mode ?? "all_party"}
                requirePrompt={meetingPolicy?.meeting_require_consent_prompt}
                orgAllows={meetingPolicy?.allow_desktop_meeting_capture ?? true}
                systemAudioAvailable={
                  Boolean(desktopInfo?.capabilities.audio.system_loopback_ready) ||
                  (!desktopInfo?.capabilities.native_capture_ready && systemAudioSupported)
                }
                sourceBusy={phase !== "ready"}
                error={meetingError}
                onCancel={() => navigate("/record")}
                onReady={(at) => {
                  meetingAnnouncedRef.current = at;
                  setMeetingError(null);
                  void handleStartClick();
                }}
              />
            </>
          ) : null}

          {!capturing ? (
            <>
              <div className="rec-card__foot">
                <span className="rec-mic-line">
                  <Icon name="microphone" size={16} />
                  <span className="rec-mic-line__label">{chosenMic?.label ?? (recorder.mode ? "Default microphone" : "No microphone yet")}</span>
                </span>
                <div className="spacer" />
                <Button variant="ghost" onClick={closePage}>
                  Cancel
                </Button>
                {/* The purple CTA is the dialog's one primary (RecordDialog.jsx); the ink record
                    pill is the rail's "Record a video". Hidden for a detected meeting, whose
                    MeetingStartPanel owns the start gate (consent first). */}
                <Button
                  icon="video-camera"
                  hidden={Boolean(meeting)}
                  disabled={phase !== "ready" || countdown !== null}
                  onClick={() => void handleStartClick()}
                >
                  {countdown !== null ? "Starting…" : "Start recording"}
                </Button>
              </div>
              <span className="gk-caption rec-start-note">
                {desktop
                  ? recorder.usingNative
                    ? "Native capture will record this display or window. Closing the window will not stop it."
                    : "The desktop app will ask which screen or window to share (webview fallback)."
                  : "Your browser will ask which screen or tab to share."}
              </span>
            </>
          ) : null}

          {/* Decision #1: shown with Kbd, read from the desktop bridge when present
              (recorderHotkeys), falling back to the FR-RD5 defaults otherwise. */}
          <div className="rec-hotkeys gk-caption">
            <span>
              Start/stop <Kbd>{formatHotkey(hotkeys.startStop)}</Kbd>
            </span>
            <span>
              Pause <Kbd>{formatHotkey(hotkeys.pause)}</Kbd>
            </span>
            {desktopInfo && (
              <span>
                Mark <Kbd>{formatHotkey(hotkeys.mark)}</Kbd>
              </span>
            )}
          </div>
        </section>

        {!hideUpsell && !desktop && !capturing ? (
          <Banner
            icon="desktop"
            className="rec-upsell"
            onDismiss={() => {
              setHideUpsell(true);
              localStorage.setItem("grackle-hide-upsell", "true");
            }}
          >
            <strong>The desktop app does more.</strong> System audio on every platform, global hotkeys,
            and recording that survives losing the network.
          </Banner>
        ) : null}

        {devMode ? <RecorderDiagnosticsPanel /> : null}
          {devMode ? <PublishStatusPanel grackleId={uploadSnapshot?.grackleId ?? null} /> : null}
      </div>

      {/* DS Countdown (App.jsx): the 3-2-1 disc over the scrim. A click on the scrim pauses
          the countdown; the disc skips it. The countdown never touches the recorder or the
          network — capture starts only when it reaches zero (the effect above). */}
      {countdown !== null ? (
        <div
          className="gk-scrim rec-countdown-scrim"
          data-testid="countdown"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("button")) return;
            setCountdownPaused((p) => !p);
          }}
        >
          <Countdown
            value={Math.max(1, countdown)}
            hint={countdownPaused ? "Paused — click anywhere to continue." : "Click anywhere to pause the countdown."}
            onSkip={() => setCountdown(0)}
          />
        </div>
      ) : null}
    </div>
  );
}

const BLUR_HINTS: Record<BlurState, string | null> = {
  off: null,
  loading: "Loading background blur…",
  ready: null,
  unavailable: "Background blur isn't available in this browser — the camera records unfiltered.",
};

/** FR-RD3's draggable camera bubble. Sits over the preview and moves the *composited* bubble,
 * so what the viewer drags and what gets recorded are the same thing; the compositor applies
 * it on its next frame, which is why this works while recording as well as before it.
 *
 * The pixel math is all in cameraFx.ts, and it is not as trivial as it looks: recorder.css
 * cover-crops the preview `<video>` to a 16:9 box, so on any capture that isn't 16:9 a naive
 * "fraction of the element" mapping drops the bubble somewhere other than under the cursor.
 */
function BubbleDragLayer({
  bubble,
  overlaySize,
  onChange,
}: {
  bubble: CameraBubble;
  overlaySize: { width: number; height: number };
  onChange: (bubble: CameraBubble) => void;
}) {
  const layerRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  // Offset from the pointer to the bubble's center at grab time, so a drag started near the
  // bubble's edge doesn't snap its center under the cursor on the first move.
  const grabRef = useRef<{ dx: number; dy: number } | null>(null);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    const measure = () => setBox({ width: layer.clientWidth, height: layer.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(layer);
    return () => observer.disconnect();
  }, []);

  const aspect = overlaySize.height > 0 ? overlaySize.width / overlaySize.height : 16 / 9;
  const center = mediaFractionToPointer(bubble.x, bubble.y, box, overlaySize.width, overlaySize.height);
  const radius = bubbleScreenRadius(bubble.radiusFraction, box, overlaySize.width, overlaySize.height);

  function moveTo(clientX: number, clientY: number) {
    const layer = layerRef.current;
    const grab = grabRef.current;
    if (!layer || !grab) return;
    const rect = layer.getBoundingClientRect();
    const fraction = pointerToMediaFraction(
      clientX - rect.left + grab.dx,
      clientY - rect.top + grab.dy,
      box,
      overlaySize.width,
      overlaySize.height,
    );
    onChange(clampBubble({ ...bubble, x: fraction.x, y: fraction.y }, aspect));
  }

  function nudge(dx: number, dy: number) {
    onChange(clampBubble({ ...bubble, x: bubble.x + dx, y: bubble.y + dy }, aspect));
  }

  return (
    <div ref={layerRef} className="rec-bubble-layer">
      <button
        type="button"
        className="rec-bubble-handle"
        style={{ left: center.x - radius, top: center.y - radius, width: radius * 2, height: radius * 2 }}
        aria-label="Camera position — drag, or use the arrow keys, to move it"
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          const rect = e.currentTarget.parentElement?.getBoundingClientRect();
          grabRef.current = rect
            ? { dx: center.x - (e.clientX - rect.left), dy: center.y - (e.clientY - rect.top) }
            : { dx: 0, dy: 0 };
        }}
        onPointerMove={(e) => {
          if (!grabRef.current) return;
          moveTo(e.clientX, e.clientY);
        }}
        onPointerUp={(e) => {
          grabRef.current = null;
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        onPointerCancel={() => {
          grabRef.current = null;
        }}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 0.05 : 0.01;
          if (e.key === "ArrowLeft") nudge(-step, 0);
          else if (e.key === "ArrowRight") nudge(step, 0);
          else if (e.key === "ArrowUp") nudge(0, -step);
          else if (e.key === "ArrowDown") nudge(0, step);
          else return;
          e.preventDefault();
        }}
      >
        <span className="rec-bubble-grip" aria-hidden />
      </button>
    </div>
  );
}

function PreviewVideo({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    video.srcObject = stream;
    void video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [stream]);
  // Muted: the combined stream carries the mixed mic/system audio track, and playing that
  // back out loud would echo straight into the microphone we're recording from.
  return <video ref={ref} muted playsInline />;
}
